
import { computed, defineComponent, PropType, ref } from 'vue'
import useQuasar from 'quasar/src/composables/use-quasar.js';
import AgreementCancelAgreement from './dialogs/cancel-agreement.vue'
import InvoicesShowInvoice from '@/components/invoices/dialogs/show-invoice.vue'
import { useRoute, useRouter } from 'vue-router'
import { AgreementsRouteNames } from '@/router/routes-names'
import { useAgreementModule, Agreement } from '@/modules/agreements'
import { useInvoicesModule, InvoiceResource } from '@/modules/invoices'
import { useSessionModule } from '@/modules/session'
import moment from 'moment'
import CreateInvoicesForSingleAgreement from '@/components/agreements/dialogs/create-invoices-for-single-agreement.vue'

export default defineComponent({
    name: 'agreement-update-agreement-management',
    components: { InvoicesShowInvoice },
    props: {
        agreement: {
            type: Object as PropType<Agreement>,
            required: true
        }
    },
    async setup (props) {
        const $q = useQuasar()
        const route = useRoute()
        const router = useRouter()
        const { disableAgreementForm, enableAgreementForm, removeDraftAgreement, markAsReadyForPublication } = useAgreementModule()
        const { getInvoicePreview, invoicePreview } = useInvoicesModule()
        const { profile, isOperatorInvoicingManager } = useSessionModule()

        const invoicePreviewVisible = ref(false)
        const agreementId = route.params.agreementId as string

        function onManageMembers () {
            router.push({ name: AgreementsRouteNames.MEMBERS, params: { agreementId: agreementId }, query: { ...route.query } })
        }

        function onChangeBillingInfo () {
            router.push({ name: AgreementsRouteNames.BILLING_INFO, params: { agreementId: agreementId }, query: { ...route.query } })
        }

        function onCancelAgreement () {
            $q.dialog({
                component: AgreementCancelAgreement,
                componentProps: {
                    id: agreementId
                }
            })
        }

        async function onInvoicePreview () {
            try {
                await getInvoicePreview({
                    resourceId: agreementId,
                    resourceType: InvoiceResource.Agreement,
                    issueDate: moment().format('YYYY-MM-DD'),
                    operatorId: profile.value?.id || ''
                })
                invoicePreviewVisible.value = true
            } catch (error) {
                $q.notify({
                    color: 'negative',
                    message: error.toString()
                })
            }
        }

        const isAgreementExpired = computed(() => props.agreement.status === 'expired')
        const isAbleToCreateInvoice = computed(() => {
            return !!isOperatorInvoicingManager.value && props.agreement.status === 'active'
        })

        async function onDisableAgreementForm () {
            await disableAgreementForm(agreementId)
            $q.notify({
                color: 'positive',
                message: 'Agreement form has been disabled'
            })
        }

        async function onEnableAgreementForm () {
            await enableAgreementForm(agreementId)
            $q.notify({
                color: 'positive',
                message: 'Agreement form has been enabled'
            })
        }

        const markAsReadyForPublicationRequest = async () => markAsReadyForPublication(agreementId, true)

        function onMarkAsReadyForPublication () {
            $q.dialog({
                title: 'Mark as ready for publication',
                message: 'Are you sure you want to mark agreement as ready for publication?',
                cancel: true,
                persistent: true
            }).onOk(() => {
                markAsReadyForPublicationRequest()
                $q.notify({
                    color: 'positive',
                    message: 'Agreement marked as ready for publication'
                })
            })
        }

        function onCreateInvoiceForAgreement () {
            $q.dialog({
                component: CreateInvoicesForSingleAgreement,
                componentProps: {
                    agreementId: agreementId
                }
            })
        }

        function onRemoveDraft () {
            $q.dialog({
                title: 'Remove draft agreement',
                message: 'Are you sure you want to delete this draft agreement permanently?',
                cancel: true,
                persistent: true
            }).onOk(() => {
                removeDraftAgreement(agreementId)
                $q.notify({
                    color: 'positive',
                    message: 'Agreement was removed permanently'
                })
                router.push({
                    name: AgreementsRouteNames.LIST,
                    query: { ...route.query },
                    params: { restoreFilters: 1 }
                })
            })
        }

        return {
            onManageMembers,
            onChangeBillingInfo,
            onCancelAgreement,
            onInvoicePreview,
            onDisableAgreementForm,
            onEnableAgreementForm,
            onMarkAsReadyForPublication,
            onRemoveDraft,
            onCreateInvoiceForAgreement,
            agreementId,
            invoicePreviewVisible,
            invoicePreview,
            isAgreementExpired,
            isAbleToCreateInvoice
        }
    }
})
